import React, { useCallback, useEffect, useMemo, useRef, useState, useLayoutEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router';
import { toast } from 'react-toastify';
import {
  LuMoon,
  LuSun,
  LuMenu,
  LuMonitor,
  LuCompass,
  LuSettings,
  LuSparkles,
  LuX,
  LuArrowLeft,
  LuArrowRight,
  LuCheck,
  LuFlag,
} from 'react-icons/lu';
import { createPortal } from 'react-dom';
import axiosInstance from '../../api/axiosInstance';
import { useProject } from '../../context/ProjectContext';
import { useWorkspace } from '../../context/WorkspaceContext';
import { useOnboarding } from '../../context/OnboardingContext';
import { INTENT_TYPES, STEP_STATUS } from '../../Config/Onboarding/constant';
import { useUpdateWorkspace } from '../../hooks/useWorkspace';
import useWorkspaceRouting from '../../hooks/useWorkspaceRouting';
import { buildWorkspaceDashboardPath } from '../../utils/workspaceRouteUtils';
import useNotificationSocket from '../../hooks/useNotificationSocket';
import { NOTIFICATIONS_REFRESH_EVENT } from '../../utils/notificationEvents';
import { PROFILE_PHOTO_UPDATED_EVENT } from '../../utils/profileEvents';
import { PROJECTS_REFRESH_EVENT } from '../../utils/projectEvents';
import formatTimestamp from '../../utils/formatTimestamp';
import { isTourPending, markTourCompleted } from '../../utils/productTourStatus';
import { useTheme } from '../../theme';
import ContextSelect from './ContextSelect';
import CommandPalette from './CommandPalette';
import { IconBell, IconSearch, IconLogout } from './NavIcons';

const PROJECT_PENDING_KEY = 'robitest_project_tour_pending';
const START_EVENT = 'robitest:start-tour';
const SPOT_PAD = 6;
const GAP = 14;
const EDGE = 12;

const storage = {
  get: (key) => {
    try {
      return localStorage.getItem(key);
    } catch {
      return null;
    }
  },
  set: (key, value) => {
    try {
      localStorage.setItem(key, value);
    } catch {
      /* ignore */
    }
  },
  remove: (key) => {
    try {
      localStorage.removeItem(key);
    } catch {
      /* ignore */
    }
  },
};

export const startProductTour = () => {
  window.dispatchEvent(new CustomEvent(START_EVENT));
};

const projectPendingKey = (userKey) => `${PROJECT_PENDING_KEY}:${userKey}`;

const queryTarget = (id) => (id ? document.querySelector(`[data-tour="${id}"]`) : null);

const findTarget = (id) => {
  const el = queryTarget(id);
  if (!el) return null;
  const r = el.getBoundingClientRect();
  if (r.width < 4 || r.height < 4) return null;
  if (r.right < 0 || r.bottom < 0 || r.left > window.innerWidth || r.top > window.innerHeight) return null;
  return el;
};

// Sidebar sections may sit below the fold; they only need to exist and be laid out.
const hasTarget = (step) => {
  if (!step.target) return true;
  if (!step.scroll) return Boolean(findTarget(step.target));
  const el = queryTarget(step.target);
  return Boolean(el && el.getClientRects().length && el.getBoundingClientRect().left >= 0);
};

const buildWelcomeSteps = (mode, firstName, { chainProject }) => {
  const isProject = mode === 'project';
  const steps = [
    {
      id: 'welcome',
      title: firstName ? `Welcome to RobiTest, ${firstName}` : 'Welcome to RobiTest',
      body: 'Your setup is complete. Take a quick tour of where everything lives — it takes less than a minute.',
      intro: true,
    },
    {
      id: 'context',
      target: 'sidebar-context',
      placement: 'right',
      peek: true,
      title: isProject ? 'Your current project' : 'Your current workspace',
      body: isProject
        ? 'This shows the project you are working in and the workspace it belongs to.'
        : 'Everything you see is scoped to this workspace — its projects, members and settings.',
    },
    {
      id: 'nav',
      target: 'sidebar-nav',
      placement: 'right',
      peek: true,
      title: 'Navigate your testing lifecycle',
      body: isProject
        ? 'Define requirements, build test cases and suites, run executions and analyze reports — grouped in the order you work.'
        : 'Jump between workspace home, projects and settings. Open a project to see its full testing toolkit.',
    },
    {
      id: 'menu',
      target: 'topbar-menu',
      placement: 'bottom',
      title: 'Open navigation',
      body: isProject
        ? 'Tap here to reach requirements, test cases, executions, reports and settings.'
        : 'Tap here to reach workspace home, projects and settings.',
    },
    {
      id: 'switcher',
      target: 'topbar-context',
      placement: 'bottom',
      title: 'Switch workspace & project',
      body: 'Use these selectors to move between workspaces and projects at any time.',
    },
    {
      id: 'search',
      target: 'topbar-search',
      placement: 'bottom',
      title: 'Search everything',
      body: 'Find projects, tests and runs, or jump to any page. Press ⌘K (Ctrl K) from anywhere.',
    },
    {
      id: 'notifications',
      target: 'topbar-notifications',
      placement: 'bottom',
      title: 'Stay in the loop',
      body: 'Execution results, schedules and team updates show up here.',
    },
    {
      id: 'theme',
      target: 'topbar-theme',
      placement: 'bottom',
      title: 'Light or dark',
      body: 'Pick a light, dark or system theme — whatever is easiest on your eyes.',
    },
    {
      id: 'profile',
      target: 'topbar-user',
      placement: 'bottom',
      title: 'Your account',
      body: 'Open settings, sign out, or replay this tour whenever you need a refresher.',
    },
    {
      id: 'done',
      title: 'You are all set',
      body: isProject
        ? 'Start by creating or running your first tests. You can reopen this tour from the account menu.'
        : 'Select a project to explore its testing tools — we will show you around the first time you open one.',
      final: true,
    },
  ];
  // The project tour covers the sidebar in depth, so hand over to it instead of summarising here.
  if (chainProject) return steps.filter((s) => s.id !== 'nav' && s.id !== 'done');
  return steps;
};

const buildProjectSteps = (projectName) => [
  {
    id: 'project-intro',
    title: projectName ? `Explore ${projectName}` : 'Explore your project',
    body: 'Your project has its own toolkit in the sidebar. Here is what each section is for.',
    intro: true,
    icon: 'compass',
  },
  {
    id: 'sec-project',
    target: 'sidebar-section-project',
    placement: 'right',
    peek: true,
    scroll: true,
    title: 'Overview',
    items: [{ label: 'Overview', desc: 'Project health at a glance — pass rate, coverage and recent runs.' }],
  },
  {
    id: 'sec-define',
    target: 'sidebar-section-define',
    placement: 'right',
    peek: true,
    scroll: true,
    title: 'Define what to test',
    items: [
      { label: 'Requirements', desc: 'Capture the features and acceptance criteria to cover.' },
      { label: 'Epics & Stories', desc: 'Organise requirements by epic and user story.' },
      { label: 'Jira', desc: 'Bring in issues from your connected Jira project.' },
    ],
  },
  {
    id: 'sec-build',
    target: 'sidebar-section-build',
    placement: 'right',
    peek: true,
    scroll: true,
    title: 'Build your tests',
    items: [
      { label: 'Test Cases', desc: 'Write, generate and maintain individual test cases.' },
      { label: 'Test Suites', desc: 'Group related cases — smoke, regression, feature sets.' },
      { label: 'Test Plans', desc: 'Scope what gets tested for a release or sprint.' },
    ],
  },
  {
    id: 'sec-run',
    target: 'sidebar-section-run',
    placement: 'right',
    peek: true,
    scroll: true,
    title: 'Run them',
    items: [
      { label: 'Executions', desc: 'Start runs and inspect results, steps and logs.' },
      { label: 'Schedules', desc: 'Run suites automatically on a recurring schedule.' },
    ],
  },
  {
    id: 'sec-analyze',
    target: 'sidebar-section-analyze',
    placement: 'right',
    peek: true,
    scroll: true,
    title: 'Analyze results',
    items: [
      { label: 'Reports', desc: 'Trends, pass rates and shareable execution reports.' },
      { label: 'AI & Learning', desc: 'AI-driven insights learned from your test history.' },
    ],
  },
  {
    id: 'sec-configure',
    target: 'sidebar-section-configure',
    placement: 'right',
    peek: true,
    scroll: true,
    title: 'Configure the project',
    items: [
      { label: 'Environments', desc: 'Target URLs and settings for each environment.' },
      { label: 'Test Data', desc: 'Reusable data sets for your tests.' },
      { label: 'Integrations', desc: 'Connect Jira, repositories and other tools.' },
      { label: 'Repository', desc: 'Link the code repository for this project.' },
      { label: 'Automation', desc: 'Set up the agent that executes your tests.' },
      { label: 'Settings', desc: 'Project details, members and preferences.' },
    ],
  },
  {
    id: 'sec-workspace',
    target: 'sidebar-section-workspace',
    placement: 'right',
    peek: true,
    scroll: true,
    title: 'Back to the workspace',
    items: [
      { label: 'Workspace Home', desc: 'Activity across every project in this workspace.' },
      { label: 'All Projects', desc: 'Open or create another project.' },
      { label: 'All Workspaces', desc: 'Switch to a different workspace.' },
    ],
  },
  {
    id: 'project-done',
    title: 'Ready to test',
    body: 'A good first step is adding requirements or creating your first test case. Replay tours anytime from the account menu.',
    final: true,
  },
];

const placePopover = (rect, size, placement) => {
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const { width: pw, height: ph } = size;
  const clampX = (x) => Math.min(Math.max(x, EDGE), vw - pw - EDGE);
  const clampY = (y) => Math.min(Math.max(y, EDGE), vh - ph - EDGE);

  if (!rect) {
    return { left: (vw - pw) / 2, top: Math.max(EDGE, (vh - ph) / 2), side: 'center' };
  }

  const fits = {
    right: rect.right + GAP + pw <= vw - EDGE,
    left: rect.left - GAP - pw >= EDGE,
    bottom: rect.bottom + GAP + ph <= vh - EDGE,
    top: rect.top - GAP - ph >= EDGE,
  };
  const order = placement === 'right' ? ['right', 'bottom', 'left', 'top'] : ['bottom', 'top', 'left', 'right'];
  const side = order.find((s) => fits[s]) || 'bottom';

  const centerY = rect.top + rect.height / 2 - ph / 2;
  const centerX = rect.left + rect.width / 2 - pw / 2;
  // Tall targets (e.g. the sidebar nav) read better with the card near their top.
  const sideY = rect.height > ph * 1.6 ? rect.top + 24 : centerY;

  switch (side) {
    case 'right':
      return { left: rect.right + GAP, top: clampY(sideY), side };
    case 'left':
      return { left: rect.left - GAP - pw, top: clampY(sideY), side };
    case 'top':
      return { left: clampX(centerX), top: rect.top - GAP - ph, side };
    default:
      return { left: clampX(centerX), top: Math.min(rect.bottom + GAP, vh - ph - EDGE), side };
  }
};

const waitForShell = (onReady) => {
  let tries = 0;
  let timer;
  const attempt = () => {
    tries += 1;
    const blocked = document.querySelector('[data-rt-modal-overlay]');
    const ready = document.querySelector('.rt2-topbar');
    if (!blocked && ready) {
      onReady();
      return;
    }
    if (tries < 40) timer = setTimeout(attempt, 500);
  };
  timer = setTimeout(attempt, 900);
  return () => clearTimeout(timer);
};

const ProductTour = ({ mode = 'workspace', user, projectName, onPeekSidebar }) => {
  const [tour, setTour] = useState(null);
  const [steps, setSteps] = useState([]);
  const [index, setIndex] = useState(0);
  const [rect, setRect] = useState(null);
  const [pos, setPos] = useState(null);
  const cardRef = useRef(null);
  const primaryRef = useRef(null);
  const chainRef = useRef(false);

  const isProject = mode === 'project';
  const userKey = String(user?.id || user?.user_id || user?.email || 'anon');

  const firstName = useMemo(() => {
    const raw = user?.first_name || user?.name || user?.username || '';
    return String(raw).trim().split(/\s+/)[0] || '';
  }, [user]);

  const projectTourPending = useCallback(() => storage.get(projectPendingKey(userKey)) === '1', [userKey]);

  const openTour = useCallback((kind, list) => {
    const visible = list.filter(hasTarget);
    if (!visible.some((s) => s.target)) return false;
    // Clear on open so other tabs and reloads don't show it again.
    if (kind === 'project') storage.remove(projectPendingKey(userKey));
    setSteps(visible);
    setIndex(0);
    setRect(null);
    setPos(null);
    setTour(kind);
    return true;
  }, [userKey]);

  const beginProject = useCallback(
    () => openTour('project', buildProjectSteps(projectName)),
    [openTour, projectName]
  );

  const beginWelcome = useCallback(() => {
    const chainProject = isProject && hasTarget({ target: 'sidebar-section-build', scroll: true });
    chainRef.current = chainProject;
    openTour('welcome', buildWelcomeSteps(mode, firstName, { chainProject }));
  }, [isProject, openTour, mode, firstName]);

  const close = useCallback(
    ({ completed = false } = {}) => {
      const finished = tour;
      setTour(null);
      setRect(null);
      setPos(null);
      if (finished === 'welcome') {
        // Skip, the close button and Escape all count as the user's choice.
        markTourCompleted();
        if (completed && chainRef.current && beginProject()) return;
        // Finishing in a workspace promises a project tour on the first project visit.
        if (completed && !isProject) storage.set(projectPendingKey(userKey), '1');
      }
      queryTarget('sidebar-nav')?.scrollTo({ top: 0 });
      onPeekSidebar?.(false);
    },
    [tour, beginProject, isProject, userKey, onPeekSidebar]
  );

  const startersRef = useRef({ beginWelcome, beginProject });
  startersRef.current = { beginWelcome, beginProject };

  // The welcome tour shows until the backend records tour_completed; the project tour follows on from it.
  useEffect(() => {
    if (tour) return undefined;
    if (isTourPending()) return waitForShell(() => startersRef.current.beginWelcome());
    if (isProject && projectTourPending()) return waitForShell(() => startersRef.current.beginProject());
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isProject, userKey]);

  useEffect(() => {
    const onStart = () => beginWelcome();
    window.addEventListener(START_EVENT, onStart);
    return () => window.removeEventListener(START_EVENT, onStart);
  }, [beginWelcome]);

  const step = tour ? steps[index] : null;

  useEffect(() => {
    onPeekSidebar?.(Boolean(tour && step?.peek));
  }, [tour, step, onPeekSidebar]);

  useEffect(() => () => onPeekSidebar?.(false), [onPeekSidebar]);

  // Track the highlighted element through sidebar expansion, resizes and scrolling.
  useEffect(() => {
    if (!step) return undefined;
    let frame;
    const measure = () => {
      if (step.scroll) {
        const target = queryTarget(step.target);
        const r = target?.getBoundingClientRect();
        if (r && (r.top < 60 || r.bottom > window.innerHeight - 8)) {
          target.scrollIntoView({ block: 'nearest' });
        }
      }
      const el = findTarget(step.target);
      if (!el) {
        setRect(null);
        return;
      }
      const r = el.getBoundingClientRect();
      setRect({ top: r.top, left: r.left, width: r.width, height: r.height, right: r.right, bottom: r.bottom });
    };
    const schedule = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(measure);
    };
    measure();
    const settle = [120, 260, 420].map((ms) => setTimeout(schedule, ms));
    const el = queryTarget(step.target);
    const ro = el && typeof ResizeObserver !== 'undefined' ? new ResizeObserver(schedule) : null;
    if (ro) ro.observe(el);
    window.addEventListener('resize', schedule);
    window.addEventListener('scroll', schedule, true);
    return () => {
      cancelAnimationFrame(frame);
      settle.forEach(clearTimeout);
      ro?.disconnect();
      window.removeEventListener('resize', schedule);
      window.removeEventListener('scroll', schedule, true);
    };
  }, [step]);

  const spot = useMemo(() => {
    if (!rect) return null;
    const top = Math.max(2, rect.top - SPOT_PAD);
    const left = Math.max(2, rect.left - SPOT_PAD);
    const right = Math.min(window.innerWidth - 2, rect.right + SPOT_PAD);
    const bottom = Math.min(window.innerHeight - 2, rect.bottom + SPOT_PAD);
    return { top, left, right, bottom, width: right - left, height: bottom - top };
  }, [rect]);

  useLayoutEffect(() => {
    if (!step || !cardRef.current) return;
    const { offsetWidth: width, offsetHeight: height } = cardRef.current;
    setPos(placePopover(spot, { width, height }, step.placement));
  }, [step, spot]);

  useEffect(() => {
    if (step) primaryRef.current?.focus({ preventScroll: true });
  }, [step]);

  const next = useCallback(() => {
    if (tour === 'welcome' && index === 0) markTourCompleted();
    if (index >= steps.length - 1) close({ completed: true });
    else setIndex((i) => i + 1);
  }, [tour, index, steps.length, close]);

  const back = useCallback(() => setIndex((i) => Math.max(0, i - 1)), []);

  useEffect(() => {
    if (!tour) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        close();
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        next();
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        back();
      }
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [tour, next, back, close]);

  if (!tour || !step) return null;

  const isFirst = index === 0;
  const isLast = index === steps.length - 1;
  const centered = !spot;
  const targeted = steps.filter((s) => s.target);
  const progressIndex = targeted.indexOf(step) + 1;
  const handsOver = tour === 'welcome' && isLast && chainRef.current;
  const BadgeIcon = step.final ? LuCheck : step.icon === 'compass' ? LuCompass : LuSparkles;

  let primaryLabel = (
    <>
      Next <LuArrowRight size={14} />
    </>
  );
  if (isFirst && step.intro) {
    primaryLabel = (
      <>
        Start tour <LuArrowRight size={14} />
      </>
    );
  } else if (handsOver) {
    primaryLabel = (
      <>
        Explore project <LuArrowRight size={14} />
      </>
    );
  } else if (isLast) {
    primaryLabel = 'Get started';
  }

  return createPortal(
    <div className="rt-tour" data-rt-modal-keep-escape>
      <div className={`rt-tour__scrim${centered ? ' rt-tour__scrim--full' : ''}`} onClick={(e) => e.stopPropagation()} />
      {spot && (
        <div
          className="rt-tour__spot"
          style={{ top: spot.top, left: spot.left, width: spot.width, height: spot.height }}
          aria-hidden="true"
        />
      )}
      <div
        ref={cardRef}
        className={`rt-tour__card rt-tour__card--${pos?.side || 'center'}${centered ? ' rt-tour__card--hero' : ''}${
          step.items ? ' rt-tour__card--list' : ''
        }`}
        style={{ top: pos?.top ?? -9999, left: pos?.left ?? -9999, visibility: pos ? 'visible' : 'hidden' }}
        role="dialog"
        aria-modal="true"
        aria-labelledby="rt-tour-title"
        aria-describedby="rt-tour-body"
        key={`${tour}-${step.id}`}
      >
        <button type="button" className="rt-tour__close" onClick={() => close()} aria-label="Close tour">
          <LuX size={16} />
        </button>
        {centered && (
          <span className={`rt-tour__badge${step.final ? ' rt-tour__badge--done' : ''}`} aria-hidden="true">
            <BadgeIcon size={20} />
          </span>
        )}
        {!centered && progressIndex > 0 && (
          <div className="rt-tour__eyebrow">
            {tour === 'project' ? 'Project tour · ' : ''}Step {progressIndex} of {targeted.length}
          </div>
        )}
        <h2 id="rt-tour-title" className="rt-tour__title">{step.title}</h2>
        <div id="rt-tour-body">
          {step.body && <p className="rt-tour__body">{step.body}</p>}
          {step.items && (
            <ul className="rt-tour__list">
              {step.items.map((item) => (
                <li key={item.label}>
                  <strong>{item.label}</strong>
                  <span>{item.desc}</span>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="rt-tour__footer">
          {!centered && targeted.length > 0 ? (
            <div className="rt-tour__dots" aria-hidden="true">
              {targeted.map((s, i) => (
                <span key={s.id} className={`rt-tour__dot${i + 1 === progressIndex ? ' rt-tour__dot--active' : ''}`} />
              ))}
            </div>
          ) : (
            <span />
          )}
          <div className="rt-tour__actions">
            {isFirst ? (
              <button type="button" className="rt-btn rt-btn--tertiary rt-btn--sm" onClick={() => close()}>
                Skip tour
              </button>
            ) : (
              !(isLast && centered) && (
                <button type="button" className="rt-btn rt-btn--secondary rt-btn--sm" onClick={back}>
                  <LuArrowLeft size={14} /> Back
                </button>
              )
            )}
            <button ref={primaryRef} type="button" className="rt-btn rt-btn--primary rt-btn--sm" onClick={next}>
              {primaryLabel}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export { ProductTour };

const isNotificationRead = (notification) =>
  notification?.read === true ||
  notification?.is_read === true ||
  notification?.status === 'read';

/** API body is `{ status, data: { notifications, unread_count } }`; accept the unwrapped shape too. */
const resolveUnreadNotifications = (body) => {
  const data =
    Array.isArray(body?.notifications) || typeof body?.unread_count === 'number'
      ? body
      : body?.data ?? body;
  const notifications = Array.isArray(data?.notifications) ? data.notifications : [];
  if (typeof data?.unread_count === 'number') {
    return { notifications, count: data.unread_count };
  }
  if (typeof data?.unreadCount === 'number') {
    return { notifications, count: data.unreadCount };
  }
  const unread = notifications.filter((n) => !isNotificationRead(n));
  return { notifications: unread, count: unread.length };
};

const SOCKET_SYNC_MIN_INTERVAL_MS = 5000;

const shouldSkip = () =>
  !localStorage.getItem('isAuthenticated') ||
  sessionStorage.getItem('isLoggingOut') === 'true';

const TopBar = ({ user, onLogout, commands = [], onOpenMobileNav }) => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { theme, resolvedTheme, setTheme, toggleTheme } = useTheme();
  const { currentWorkspace, selectWorkspace, userWorkspaces, setUserWorkspaces } = useWorkspace();
  const { mutate: updateWorkspaceFn } = useUpdateWorkspace();
  const { selectedProject, selectProject, clearProject, updateProjects } = useProject();
  const { getProjectIdFromURL, getWorkspaceIdFromURL } = useWorkspaceRouting();
  const { resetOnboarding, initializeStepFlow, markStepCompleted, setStepStatus, nextStep } = useOnboarding();

  const [workspacesList, setWorkspacesList] = useState([]);
  const [workspacesLoading, setWorkspacesLoading] = useState(false);
  const [projectsList, setProjectsList] = useState([]);
  const [projectsLoading, setProjectsLoading] = useState(false);
  const [cmdOpen, setCmdOpen] = useState(false);
  const [notifyOpen, setNotifyOpen] = useState(false);
  const [userOpen, setUserOpen] = useState(false);
  const [themeOpen, setThemeOpen] = useState(false);
  const [unreadNotifications, setUnreadNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [profilePhoto, setProfilePhoto] = useState(user?.profile_photo || null);

  const notifyRef = useRef(null);
  const userRef = useRef(null);
  const themeRef = useRef(null);
  const lastSocketSyncAtRef = useRef(0);

  // Listing pages are for choosing, so the switcher shows nothing selected there
  // (the stored selection is kept for the rest of the app).
  const isAllWorkspacesPage = /\/dashboard\/all-workspaces\/?$/.test(pathname);
  const isProjectsListPage = /\/dashboard\/projects\/?$/.test(pathname);

  const workspaceId = getWorkspaceIdFromURL() || currentWorkspace?.id;
  const urlProjectId = getProjectIdFromURL();
  const projectId = urlProjectId || selectedProject?.id || null;
  const shownWorkspaceId = isAllWorkspacesPage ? null : workspaceId;
  const shownProjectId = isAllWorkspacesPage || isProjectsListPage ? null : projectId;

  const extractWorkspacePage = (response) => {
    const responseData = response?.data ?? response;
    const batch = Array.isArray(responseData)
      ? responseData
      : Array.isArray(responseData?.workspaces)
        ? responseData.workspaces
        : Array.isArray(responseData?.data)
          ? responseData.data
          : Array.isArray(response?.workspaces)
            ? response.workspaces
            : Array.isArray(response?.data)
              ? response.data
              : [];
    const pageSize =
      Number(
        response?.page_size ??
          responseData?.page_size ??
          response?.pagination?.page_size ??
          responseData?.pagination?.page_size ??
          batch.length ??
          100
      ) || 100;
    const totalCount = Number(
      response?.total_count ??
        responseData?.total_count ??
        response?.total ??
        responseData?.total ??
        response?.pagination?.total_count ??
        responseData?.pagination?.total_count ??
        0
    );
    const totalPagesFromApi = Number(
      response?.total_pages ??
        responseData?.total_pages ??
        response?.pagination?.total_pages ??
        responseData?.pagination?.total_pages ??
        0
    );
    const totalPages =
      totalPagesFromApi > 0
        ? totalPagesFromApi
        : totalCount > 0
          ? Math.ceil(totalCount / pageSize)
          : batch.length < pageSize
            ? 1
            : 2;
    return { batch, pageSize, totalPages };
  };

  /** Load every workspace page so the top-bar dropdown is complete. */
  const fetchAllWorkspaces = useCallback(async (search = '') => {
    if (shouldSkip()) return;
    setWorkspacesLoading(true);
    try {
      const pageSize = 100;
      let page = 1;
      let totalPages = 1;
      const all = [];
      const seen = new Set();

      do {
        const params = { page, page_size: pageSize };
        if (search.trim()) params.search = search.trim();
        const response = await axiosInstance.get('/workspace/list_workspaces', { params });
        const { batch, totalPages: pages } = extractWorkspacePage(response);
        totalPages = Math.max(1, pages);
        batch.forEach((ws) => {
          const id = ws?.id ?? ws?.workspace_id;
          if (id == null || seen.has(String(id))) return;
          seen.add(String(id));
          all.push(ws);
        });
        // Safety: stop if API returns empty or we hit a hard ceiling
        if (batch.length === 0 || page >= 50) break;
        page += 1;
      } while (page <= totalPages);

      setWorkspacesList(all);
      if (!search.trim() && typeof setUserWorkspaces === 'function') {
        setUserWorkspaces(all);
      }
    } catch (error) {
      console.error('Failed to load workspaces for top bar', error);
    } finally {
      setWorkspacesLoading(false);
    }
  }, [setUserWorkspaces]);

  /** Load every active project page for the current workspace. */
  const fetchAllProjects = useCallback(async (search = '') => {
    if (shouldSkip() || !workspaceId) {
      setProjectsList([]);
      return;
    }
    setProjectsLoading(true);
    try {
      const pageSize = 100;
      let page = 1;
      let totalPages = 1;
      const all = [];
      const seen = new Set();

      do {
        const params = {
          page,
          page_size: pageSize,
          pro_status: 'active',
          workspace_id: workspaceId,
        };
        if (search.trim()) params.search = search.trim();
        const response = await axiosInstance.get('/projects/list_project', { params });
        const batch = response?.projects || [];
        const totalCount = Number(response?.total_count ?? response?.count ?? 0);
        const totalPagesFromApi = Number(response?.total_pages ?? 0);
        totalPages =
          totalPagesFromApi > 0
            ? totalPagesFromApi
            : totalCount > 0
              ? Math.ceil(totalCount / pageSize)
              : batch.length < pageSize
                ? page
                : page + 1;

        batch.forEach((p) => {
          const id = p?.project_id ?? p?.id;
          if (id == null || seen.has(String(id))) return;
          seen.add(String(id));
          all.push(p);
        });
        if (batch.length === 0 || page >= 50) break;
        page += 1;
      } while (page <= totalPages);

      setProjectsList(all);
      if (!search.trim()) updateProjects(all);
    } catch (error) {
      console.error('Failed to load projects for top bar', error);
    } finally {
      setProjectsLoading(false);
    }
  }, [workspaceId, updateProjects]);

  const workspaces = useMemo(() => {
    if (workspacesList.length > 0) return workspacesList;
    if (Array.isArray(userWorkspaces) && userWorkspaces.length > 0) return userWorkspaces;
    return [];
  }, [workspacesList, userWorkspaces]);

  const workspaceLabel = isAllWorkspacesPage
    ? 'Select workspace'
    : currentWorkspace?.workspace_name ||
      workspaces.find((w) => String(w.id) === String(workspaceId))?.workspace_name ||
      'Workspace';

  const listedProject = projectsList.find(
    (p) => String(p.project_id ?? p.id) === String(shownProjectId)
  );
  const contextMatchesSelection =
    selectedProject && String(selectedProject.id) === String(shownProjectId);
  const projectLabel = !shownProjectId
    ? 'Select project'
    : listedProject?.project_name ||
      listedProject?.name ||
      (contextMatchesSelection
        ? selectedProject.name || selectedProject.project_name
        : null) ||
      (projectsLoading ? 'Loading…' : `Project ${shownProjectId}`);

  const homePath = workspaceId
    ? buildWorkspaceDashboardPath(workspaceId, projectId || null)
    : '/dashboard';

  useEffect(() => {
    fetchAllWorkspaces();
  }, [fetchAllWorkspaces]);

  useEffect(() => {
    fetchAllProjects();
    const handleRefresh = () => fetchAllProjects();
    window.addEventListener(PROJECTS_REFRESH_EVENT, handleRefresh);
    return () => window.removeEventListener(PROJECTS_REFRESH_EVENT, handleRefresh);
  }, [fetchAllProjects]);

  // Header label follows the project in the URL, not the previous selection.
  useEffect(() => {
    if (!shownProjectId || projectsLoading) return;
    const match = projectsList.find(
      (p) => String(p.project_id ?? p.id) === String(shownProjectId)
    );
    const name = match?.project_name || match?.name;
    if (!name) return;
    if (
      String(selectedProject?.id) === String(shownProjectId) &&
      (selectedProject?.name === name || selectedProject?.project_name === name)
    ) {
      return;
    }
    selectProject(String(shownProjectId), name);
  }, [
    shownProjectId,
    projectsList,
    projectsLoading,
    selectedProject?.id,
    selectedProject?.name,
    selectedProject?.project_name,
    selectProject,
  ]);

  const fetchNotifications = useCallback(async () => {
    if (shouldSkip()) return;
    try {
      const response = await axiosInstance.get('/user_profile/list_user_notifications', {
        skipGlobalLoader: true,
      });
      const { notifications, count } = resolveUnreadNotifications(response);
      setUnreadNotifications(notifications);
      setUnreadCount(count);
    } catch (error) {
      if (error?.response?.status !== 401) {
        console.error('Failed to fetch notifications', error);
      }
    }
  }, []);

  useEffect(() => {
    fetchNotifications();
    const handleRefresh = () => setTimeout(() => fetchNotifications(), 400);
    window.addEventListener(NOTIFICATIONS_REFRESH_EVENT, handleRefresh);
    return () => window.removeEventListener(NOTIFICATIONS_REFRESH_EVENT, handleRefresh);
  }, [fetchNotifications]);

  const socketUserId =
    user?.userId ||
    user?.id ||
    (user ? JSON.parse(localStorage.getItem('user') || '{}').userId : null);
  const {
    latestNotification,
    markAllAsRead: markAllSocketNotificationsRead,
    disconnect: disconnectNotifications,
  } = useNotificationSocket(socketUserId);

  // Throttled so a burst of socket messages can't hit the API more than once per interval;
  // the latest event still gets a trailing sync.
  useEffect(() => {
    if (!latestNotification) return undefined;
    const elapsed = Date.now() - lastSocketSyncAtRef.current;
    const delay = Math.max(500, SOCKET_SYNC_MIN_INTERVAL_MS - elapsed);
    const timer = setTimeout(() => {
      lastSocketSyncAtRef.current = Date.now();
      fetchNotifications();
    }, delay);
    return () => clearTimeout(timer);
  }, [latestNotification, fetchNotifications]);

  const markNotificationRead = useCallback(async (notification) => {
    const id = notification?.id || notification?.notification_id;
    if (!id) return;
    setUnreadNotifications((prev) => prev.filter((n) => (n.id || n.notification_id) !== id));
    setUnreadCount((prev) => Math.max(0, prev - 1));
    try {
      await axiosInstance.put(
        `/user_profile/mark_as_read_notifications?notification_id=${id}`,
        null,
        { skipGlobalLoader: true }
      );
    } catch (error) {
      console.error('Failed to mark notification as read', error);
      fetchNotifications();
    }
  }, [fetchNotifications]);

  const markAllNotificationsRead = useCallback(async () => {
    setUnreadNotifications([]);
    setUnreadCount(0);
    markAllSocketNotificationsRead();
    try {
      await axiosInstance.put('/user_profile/mark_as_read_notifications', null, {
        skipGlobalLoader: true,
      });
    } catch (error) {
      console.error('Failed to mark all notifications as read', error);
      fetchNotifications();
    }
  }, [fetchNotifications, markAllSocketNotificationsRead]);

  useEffect(() => {
    const onPhoto = (e) => {
      if (e?.detail?.profile_photo) setProfilePhoto(e.detail.profile_photo);
    };
    window.addEventListener(PROFILE_PHOTO_UPDATED_EVENT, onPhoto);
    return () => window.removeEventListener(PROFILE_PHOTO_UPDATED_EVENT, onPhoto);
  }, []);

  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setCmdOpen(true);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  useEffect(() => {
    const onDoc = (e) => {
      if (notifyRef.current && !notifyRef.current.contains(e.target)) setNotifyOpen(false);
      if (userRef.current && !userRef.current.contains(e.target)) setUserOpen(false);
      if (themeRef.current && !themeRef.current.contains(e.target)) setThemeOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const workspaceItems = useMemo(
    () =>
      workspaces.map((w) => {
        const id = w.id ?? w.workspace_id;
        return {
          id,
          label: w.workspace_name || `Workspace ${id}`,
          active: shownWorkspaceId != null && String(id) === String(shownWorkspaceId),
        };
      }),
    [workspaces, shownWorkspaceId]
  );

  const projectItems = useMemo(
    () =>
      projectsList.map((p) => ({
        id: p.project_id ?? p.id,
        label: p.project_name || p.name,
        active: shownProjectId != null && String(p.project_id ?? p.id) === String(shownProjectId),
      })),
    [projectsList, shownProjectId]
  );

  const handleWorkspaceSelect = useCallback(
    async (item) => {
      const ws = workspaces.find((w) => String(w.id ?? w.workspace_id) === String(item.id));
      if (!ws) return;
      const id = ws.id ?? ws.workspace_id;
      selectWorkspace(ws);
      clearProject();
      updateWorkspaceFn({ last_active_workspace_id: id });

      try {
        const response = await axiosInstance.get('/projects/list_project', {
          params: {
            page: 1,
            page_size: 2,
            pro_status: 'active',
            workspace_id: id,
          },
        });
        const batch = response?.projects || [];
        const total = Number(response?.total_count ?? response?.count ?? batch.length);
        if (batch.length === 1 && total <= 1) {
          const project = batch[0];
          const onlyId = project.project_id ?? project.id;
          const onlyName = project.project_name || project.name || `Project ${onlyId}`;
          selectProject(String(onlyId), onlyName);
          updateWorkspaceFn({
            last_active_workspace_id: id,
            last_active_project_id: onlyId,
          });
          navigate(buildWorkspaceDashboardPath(id, onlyId));
          return;
        }
      } catch (error) {
        console.error('Failed to check projects for workspace', error);
      }

      navigate(buildWorkspaceDashboardPath(id, null, 'projects'));
    },
    [workspaces, selectWorkspace, clearProject, selectProject, updateWorkspaceFn, navigate]
  );

  const handleProjectSelect = useCallback(
    (item) => {
      if (!workspaceId) {
        toast.error('Select a workspace first');
        navigate('/dashboard/all-workspaces');
        return;
      }
      selectProject(String(item.id), item.label);
      updateWorkspaceFn({
        last_active_workspace_id: workspaceId,
        last_active_project_id: item.id,
      });
      navigate(buildWorkspaceDashboardPath(workspaceId, item.id));
      toast.success(`Opened "${item.label}"`);
    },
    [workspaceId, selectProject, updateWorkspaceFn, navigate]
  );

  /** ⌘K palette: nav actions + every workspace/project so name search works. */
  const paletteCommands = useMemo(() => {
    const nav = Array.isArray(commands) ? commands : [];
    const wsCmds = workspaceItems.map((w) => ({
      label: w.label,
      group: 'Workspaces',
      keywords: ['workspace', String(w.id), w.label],
      onSelect: () => handleWorkspaceSelect(w),
    }));
    const projectCmds = projectItems.map((p) => ({
      label: p.label,
      group: 'Projects',
      keywords: ['project', String(p.id), p.label],
      onSelect: () => handleProjectSelect(p),
    }));
    return [...nav, ...projectCmds, ...wsCmds];
  }, [commands, workspaceItems, projectItems, handleWorkspaceSelect, handleProjectSelect]);

  // Start a fresh wizard run directly at Step 2 (workspace creation)
  const handleGettingStarted = () => {
    setUserOpen(false);
    const intent = localStorage.getItem('wizard_intent') || INTENT_TYPES.JIRA_REQUIREMENTS;
    resetOnboarding();
    initializeStepFlow(intent);
    markStepCompleted(1);
    nextStep();
    setStepStatus(2, STEP_STATUS.IN_PROGRESS);
    navigate('/app/onboarding?step=2');
  };

  const userDisplayName = user?.name || user?.username || user?.email || 'User';

  const initials = useMemo(() => {
    const name = user?.name || user?.username || user?.email || 'U';
    return (
      String(name)
        .split(/\s+/)
        .slice(0, 2)
        .map((p) => p[0]?.toUpperCase() || '')
        .join('') || 'U'
    );
  }, [user]);

  const ThemeIcon = resolvedTheme === 'dark' ? LuMoon : LuSun;

  return (
    <>
      <header className="rt2-topbar">
        {typeof onOpenMobileNav === 'function' && (
          <button
            type="button"
            className="rt-btn rt-btn--tertiary rt-btn--icon rt2-topbar__menu"
            aria-label="Open navigation"
            data-tour="topbar-menu"
            onClick={onOpenMobileNav}
          >
            <LuMenu size={18} />
          </button>
        )}

        <Link to={homePath} className="rt2-topbar__brand" aria-label="RobiTest home">
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
            <path d="M12 3a6 6 0 0 1 4 10.5V17H8v-3.5A6 6 0 0 1 12 3z" />
            <path d="M9 21h6" />
          </svg>
          RobiTest
        </Link>

        <div className="rt2-topbar__sep" />

        <div className="rt2-topbar__context" data-tour="topbar-context">
          <ContextSelect
            label="Workspace"
            valueLabel={workspaceLabel}
            items={workspaceItems}
            onSelect={handleWorkspaceSelect}
            emptyLabel="No workspaces"
            placeholder="Search workspaces…"
            loading={workspacesLoading && workspacesList.length === 0}
          />
          <ContextSelect
            label="Project"
            valueLabel={projectLabel}
            items={isAllWorkspacesPage ? [] : projectItems}
            onSelect={handleProjectSelect}
            emptyLabel={isAllWorkspacesPage ? 'Select a workspace first' : 'No projects in this workspace'}
            placeholder="Search projects…"
            loading={!isAllWorkspacesPage && projectsLoading && projectsList.length === 0}
          />
          {!urlProjectId && shownProjectId ? (
            <button
              type="button"
              className="rt-btn rt-btn--primary rt-btn--sm"
              title="Open selected project"
              onClick={() => {
                const match = projectsList.find(
                  (p) => String(p.project_id) === String(projectId)
                );
                handleProjectSelect({
                  id: projectId,
                  label: match?.project_name || selectedProject?.name || `Project ${projectId}`,
                });
              }}
            >
              Open project
            </button>
          ) : null}
        </div>

        <div className="rt2-topbar__spacer" />

        <button
          type="button"
          className="rt2-topbar__search"
          data-tour="topbar-search"
          onClick={() => setCmdOpen(true)}
        >
          <IconSearch width={14} height={14} />
          <span>Search projects, tests, runs…</span>
          <kbd>⌘K</kbd>
        </button>

        <div className="rt2-topbar__actions">
          <div className="rt2-topbar__notify" ref={notifyRef}>
            <button
              type="button"
              className="rt-btn rt-btn--tertiary rt-btn--icon"
              aria-label="Notifications"
              data-tour="topbar-notifications"
              onClick={() => setNotifyOpen((v) => !v)}
            >
              <IconBell />
              {unreadCount > 0 && (
                <span className="rt2-topbar__badge">{unreadCount > 99 ? '99+' : unreadCount}</span>
              )}
            </button>
            {notifyOpen && (
              <div className="rt2-dropdown">
                {unreadNotifications.length === 0 ? (
                  <div className="rt2-empty">No unread notifications</div>
                ) : (
                  <>
                    <div className="rt2-dropdown__meta" style={{ display: 'flex', justifyContent: 'flex-end', padding: '4px 8px' }}>
                      <button type="button" className="rt2-link" onClick={markAllNotificationsRead}>
                        Mark all as read
                      </button>
                    </div>
                    {unreadNotifications.slice(0, 12).map((n) => {
                      const text =
                        n.title ||
                        (typeof n.message === 'string' ? n.message : n.message?.message) ||
                        n.notification_text ||
                        'Notification';
                      return (
                        <button
                          type="button"
                          key={n.id || n.notification_id || n.created_at}
                          className="rt2-dropdown__item"
                          onClick={() => markNotificationRead(n)}
                          title="Mark as read"
                        >
                          <div>{text}</div>
                          <div className="rt2-dropdown__meta">
                            {formatTimestamp(n.created_at || n.timestamp || n.createdAt)}
                          </div>
                        </button>
                      );
                    })}
                  </>
                )}
              </div>
            )}
          </div>

          <div className="rt2-topbar__notify" ref={themeRef}>
            <button
              type="button"
              className="rt-btn rt-btn--tertiary rt-btn--icon"
              aria-label="Theme"
              data-tour="topbar-theme"
              title={`Theme: ${theme}`}
              onClick={() => setThemeOpen((v) => !v)}
              onDoubleClick={(e) => {
                e.preventDefault();
                toggleTheme();
              }}
            >
              <ThemeIcon size={16} />
            </button>
            {themeOpen && (
              <div className="rt2-dropdown" style={{ minWidth: 160 }}>
                {[
                  { id: 'light', label: 'Light', icon: LuSun },
                  { id: 'dark', label: 'Dark', icon: LuMoon },
                  { id: 'system', label: 'System', icon: LuMonitor },
                ].map((opt) => {
                  const Icon = opt.icon;
                  return (
                    <button
                      key={opt.id}
                      type="button"
                      className={`rt2-dropdown__item${theme === opt.id ? ' rt2-dropdown__item--active' : ''}`}
                      onClick={() => {
                        setTheme(opt.id);
                        setThemeOpen(false);
                      }}
                    >
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                        <Icon size={14} /> {opt.label}
                      </span>
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          <div className="rt2-topbar__notify" ref={userRef}>
            <button
              type="button"
              className="rt2-avatar"
              aria-label="User menu"
              data-tour="topbar-user"
              onClick={() => setUserOpen((v) => !v)}
            >
              {profilePhoto ? (
                <img src={profilePhoto} alt="" />
              ) : (
                initials
              )}
            </button>
            {userOpen && (
              <div className="rt2-dropdown rt2-user-menu" role="menu">
                <div className="rt2-user-menu__header">
                  <strong className="rt2-user-menu__name">{userDisplayName}</strong>
                  {user?.email && user.email !== userDisplayName && (
                    <div className="rt2-dropdown__meta">{user.email}</div>
                  )}
                </div>
                <div className="rt2-user-menu__divider" role="separator" />
                <button
                  type="button"
                  role="menuitem"
                  className="rt2-dropdown__item rt2-user-menu__item"
                  onClick={() => {
                    setUserOpen(false);
                    if (workspaceId) {
                      navigate(buildWorkspaceDashboardPath(workspaceId, projectId, 'settings'));
                    } else {
                      navigate('/dashboard/settings');
                    }
                  }}
                >
                  <LuSettings size={15} aria-hidden="true" /> Settings
                </button>
                <button
                  type="button"
                  role="menuitem"
                  className="rt2-dropdown__item rt2-user-menu__item"
                  onClick={handleGettingStarted}
                >
                  <LuFlag size={15} aria-hidden="true" /> Getting Started
                </button>
                <button
                  type="button"
                  role="menuitem"
                  className="rt2-dropdown__item rt2-user-menu__item"
                  onClick={() => {
                    setUserOpen(false);
                    startProductTour();
                  }}
                >
                  <LuCompass size={15} aria-hidden="true" /> Take a product tour
                </button>
                <div className="rt2-user-menu__divider" role="separator" />
                <button
                  type="button"
                  role="menuitem"
                  className="rt2-dropdown__item rt2-user-menu__item rt2-user-menu__item--danger"
                  onClick={() => {
                    setUserOpen(false);
                    disconnectNotifications();
                    onLogout?.();
                  }}
                >
                  <IconLogout width={15} height={15} aria-hidden="true" /> Sign out
                </button>
              </div>
            )}
          </div>
        </div>
      </header>

      <CommandPalette open={cmdOpen} onClose={() => setCmdOpen(false)} commands={paletteCommands} />
    </>
  );
};

export default TopBar;
